import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  LinearProgress,
  Fade,
  MenuItem,
  Collapse,
  Button,
  Menu,
} from '@mui/material';
import { KeyboardArrowDown as ArrowDownIcon } from '@mui/icons-material';
import { DashboardTab, DashboardFilters, DashboardData } from '../types/dashboard.types';
import { OverviewTab } from '../tabs/OverviewTab';
import { ProfileTab } from '../tabs/ProfileTab';
import { MaintenanceTab } from '../tabs/MaintenanceTab';
import { HealthPanelTab } from '../tabs/HealthPanelTab';
import { FilesTab } from '../tabs/FilesTab';
import { InventoryTab } from '../tabs/InventoryTab';
import { CalendarTab } from '../tabs/CalendarTab';
import { TABS, TAB_BY_ID } from '../tabs/registry';
import { TabContent } from '../Dashboard.styles';
import { BRAND_COLORS, alpha } from '../../../styles/theme';
import TabErrorBoundary from './ErrorBoundary';
import { UserProfile } from '../../../lib/auth-state-machine';
import { useTranslation } from '../../../i18n';

interface TabPanelProps {
  activeTab: DashboardTab;
  onTabChange: (tab: DashboardTab) => void;
  onFilterChange: (filters: Partial<DashboardFilters>) => void;
  onResetFileFilters?: () => void;
  data: DashboardData;
  filters: DashboardFilters;
  flightDates: string[];
  isUpdating?: boolean;
  serialNumbers: string[];
  profile: UserProfile | null;
  onRefreshOverview?: () => void;
}

export const TabPanel: React.FC<TabPanelProps> = ({
  activeTab,
  onTabChange,
  onFilterChange,
  onResetFileFilters,
  data,
  filters,
  flightDates,
  isUpdating = false,
  serialNumbers,
  profile,
  onRefreshOverview,
}) => {
  const { t } = useTranslation();
  const [droneMenuAnchor, setDroneMenuAnchor] = useState<null | HTMLElement>(null);
  const [isFirstSelection, setIsFirstSelection] = useState(false);

  const handleDroneButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setDroneMenuAnchor(event.currentTarget);
  };

  const handleDroneMenuClose = () => {
    setDroneMenuAnchor(null);
  };

  const handleDroneSelect = (serial: string) => {
    const wasFirstSelection = !filters.selectedSerial;
    onFilterChange({ selectedSerial: serial });
    setDroneMenuAnchor(null);

    // Track if this is the first selection for animation purposes
    if (wasFirstSelection && serial) {
      setIsFirstSelection(true);
      // Auto-switch to Profile tab when first drone selected — only for tabs
      // flagged with autoSwitchOnDroneSelect (overview, files). Note this is
      // intentionally NOT all tier-1 tabs: inventory & calendar stay put.
      if (TAB_BY_ID[activeTab]?.autoSwitchOnDroneSelect) {
        onTabChange('profile');
      }
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: DashboardTab) => {
    onTabChange(newValue);
  };

  // Check if we have a drone selected
  const hasDroneSelected = filters.selectedSerial && filters.selectedSerial !== '';

  // Determine which tier the active tab belongs to (driven by the registry)
  const isFirstTierTab = TAB_BY_ID[activeTab]?.tier === 1;
  const isSecondTierTab = TAB_BY_ID[activeTab]?.tier === 2;

  // Render the active tab's content. The tab BAR is registry-driven (STD-013),
  // but content keeps each tab's per-tab prop signature via a switch (decision b).
  // The exhaustive `default` makes adding a tab id a compile error until a case
  // is added here — keeping the registry and the renderer in lockstep.
  const renderActiveTab = (): React.ReactNode => {
    switch (activeTab) {
      case 'overview':
        return (
          <OverviewTab data={data} onFilterChange={onFilterChange} onTabChange={onTabChange} />
        );
      case 'profile':
        return (
          <ProfileTab
            data={data}
            filters={filters}
            flightDates={flightDates}
            onFilterChange={onFilterChange}
            profile={profile}
            onRefreshData={onRefreshOverview}
          />
        );
      case 'maintenance':
        return <MaintenanceTab data={data} filters={filters} onFilterChange={onFilterChange} />;
      case 'health':
        return <HealthPanelTab data={data} filters={filters} onFilterChange={onFilterChange} />;
      case 'files':
        return (
          <FilesTab
            data={data}
            filters={filters}
            onFilterChange={onFilterChange}
            serialNumbers={serialNumbers}
            onResetFileFilters={onResetFileFilters}
          />
        );
      case 'inventory':
        return <InventoryTab />;
      case 'calendar':
        return <CalendarTab />;
      default: {
        const _exhaustive: never = activeTab;
        return _exhaustive;
      }
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Subtle loading indicator for updates */}
      <Fade in={isUpdating}>
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1,
            height: 2,
          }}
        />
      </Fade>

      {/* First tier tabs */}
      <Box sx={{ position: 'relative' }}>
        <Tabs
          value={isFirstTierTab ? activeTab : false}
          onChange={handleTabChange}
          sx={{
            minHeight: 48,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '1rem',
              color: alpha(BRAND_COLORS.forestGreen.main, 0.7),
              '&.Mui-selected': {
                color: BRAND_COLORS.forestGreen.main,
                fontWeight: 600,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: BRAND_COLORS.yellow.main,
              height: 3,
            },
          }}
        >
          {TABS.filter((tab) => tab.tier === 1).map((tab) => (
            <Tab key={tab.id} label={t(tab.labelKey)} value={tab.id} />
          ))}
        </Tabs>

        <Menu
          anchorEl={droneMenuAnchor}
          open={Boolean(droneMenuAnchor)}
          onClose={handleDroneMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {serialNumbers.map((serial) => (
            <MenuItem
              key={serial}
              onClick={() => handleDroneSelect(serial)}
              selected={serial === filters.selectedSerial}
              sx={{ minWidth: 120 }}
            >
              {serial}
            </MenuItem>
          ))}
        </Menu>

        {/* Second tier tabs - fade in only on first drone selection */}
        <Collapse in={Boolean(hasDroneSelected)} timeout={300}>
          <Fade in={Boolean(hasDroneSelected)} timeout={isFirstSelection ? 600 : 0}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Tabs
                value={isSecondTierTab && activeTab ? activeTab : false}
                onChange={handleTabChange}
                sx={{
                  minHeight: 48,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '1rem',
                    color: alpha(BRAND_COLORS.forestGreen.main, 0.7),
                    '&.Mui-selected': {
                      color: BRAND_COLORS.forestGreen.main,
                      fontWeight: 600,
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: BRAND_COLORS.yellow.main,
                    height: 3,
                  },
                }}
              >
                {TABS.filter((tab) => tab.tier === 2).map((tab) => (
                  <Tab key={tab.id} label={t(tab.labelKey)} value={tab.id} />
                ))}
              </Tabs>
              <Button
                onClick={handleDroneButtonClick}
                variant="outlined"
                endIcon={<ArrowDownIcon />}
                sx={{
                  textTransform: 'none',
                  height: 36,
                  borderColor: alpha(BRAND_COLORS.forestGreen.main, 0.3),
                  color: BRAND_COLORS.forestGreen.main,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: BRAND_COLORS.forestGreen.main,
                    backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.04),
                  },
                }}
              >
                {filters.selectedSerial}
              </Button>
            </Box>
          </Fade>
        </Collapse>
      </Box>

      {/* Main content area - fade in only on first drone selection for drone-specific tabs */}
      <Fade in={true} timeout={isSecondTierTab && isFirstSelection ? 800 : 0}>
        <Box>
          <TabContent sx={{ opacity: isUpdating ? 0.6 : 1, transition: 'opacity 0.2s' }}>
            {/* key={activeTab} remounts the boundary per tab: keeps each tab's
                crash isolated AND lets a transient crash recover when the user
                navigates away and back (a persistent crash just re-surfaces). */}
            <TabErrorBoundary key={activeTab}>{renderActiveTab()}</TabErrorBoundary>
          </TabContent>
        </Box>
      </Fade>
    </Box>
  );
};
